import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PurpleNavBar } from '../components/PurpleNavBar';
import { COLORS } from '../../../core/constants/colors';
import { api } from '../../../core/utils/apiClient';
import { CompanyMembersManager } from '../../dashboard_user/components/CompanyMembersManager';


interface PartnerCompany {
  id: number;
  company_id: string;
  name: string;
  website?: string;
  industry?: string;
  location?: string;
  founding_year?: number;
  employees?: string;
  description?: string;
  domain?: string;
  is_private: boolean;
  profile_completeness_score: number;
  created_at: string;
  owner_id: string;
  enrichment_score?: number;
  last_enrichment_at?: string;
  enriched_data?: string;
  pending_verification: boolean;
  updated_at: string;
  created_by: string;
  updated_by: string;
  is_active: boolean;
  user_role: string;
}

export const PartnerCompaniesPage: React.FC = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<PartnerCompany[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<PartnerCompany | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showMembersManager, setShowMembersManager] = useState(false);
  const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);
  const [isCreatingCompany, setIsCreatingCompany] = useState(false);
  const [newCompany, setNewCompany] = useState({
    name: '',
    domain: '',
    description: '',
    website: '',
    industry: '',
    location: '',
    founding_year: '',
    employees: '',
  });

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    // Auto-select first company when companies are loaded
    if (companies.length > 0 && !selectedCompany) {
      setSelectedCompany(companies[0]);
    }
  }, [companies, selectedCompany]);

  const loadCompanies = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get('/api/v1/companies/my-companies');

      if (response.ok) {
        const data = await response.json();
        const companiesList = Array.isArray(data) ? data : (data.companies || []);
        setCompanies(companiesList);
      } else if (response.status === 404) {
        setCompanies([]);
      } else {
        setError('Failed to load companies');
      }
    } catch (err: any) {
      console.error('Error loading companies:', err);
      setError(err.message || 'Failed to load companies');
    } finally {
      setIsLoading(false);
    }
  };

  const getCompanyInitial = (name: string) => {
    return name?.charAt(0).toUpperCase() || 'C';
  };

  const getInitialColor = (name: string) => {
    const colors = [
      '#3B82F6', '#10B981', '#F59E0B', '#EF4444', 
      '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'
    ];
    const index = name?.charCodeAt(0) % colors.length || 0;
    return colors[index];
  };

  const handleAddCompany = async () => {
    if (!newCompany.name || !newCompany.domain) {
      setError('Company name and domain are required');
      return;
    }

    setIsCreatingCompany(true);
    setError(null);

    try {
      const payload = {
        name: newCompany.name,
        domain: newCompany.domain,
        description: newCompany.description || undefined,
        website: newCompany.website || undefined,
        founding_year: newCompany.founding_year ? parseInt(newCompany.founding_year) : undefined,
        employees: newCompany.employees || undefined,
        industry: newCompany.industry || undefined,
        location: newCompany.location || undefined,
        linkedin_url: '',
        auto_enrich: true,
      };

      const response = await api.post('/api/v1/companies/', payload);

      if (response.ok) {
        const createdCompany = await response.json();
        setShowAddCompanyModal(false);
        setNewCompany({
          name: '',
          domain: '',
          description: '',
          website: '',
          industry: '',
          location: '',
          founding_year: '',
          employees: '',
        });
        // Reload companies list
        await loadCompanies();
        // Select the newly created company
        setSelectedCompany(createdCompany);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to create company');
      }
    } catch (err: any) {
      console.error('Error creating company:', err);
      setError(err.message || 'Failed to create company');
    } finally {
      setIsCreatingCompany(false);
    }
  };

  const formatWebsiteUrl = (url: string) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `https://${url}`;
  };

  const canManageCompany = (company: PartnerCompany) => {
    return company.user_role === 'owner' || company.user_role === 'admin';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen" style={{ fontFamily: 'DM Sans, sans-serif' }}>
        <PurpleNavBar />
        <div className="min-h-screen bg-white flex items-center justify-center">
          <div
            className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent"
            style={{ borderColor: COLORS.user.primary, borderTopColor: 'transparent' }}
          />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen" style={{ fontFamily: 'DM Sans, sans-serif' }}>
        <PurpleNavBar />
        <div className="min-h-screen bg-white flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-500 text-lg mb-4">{error}</p>
            <button
              onClick={loadCompanies}
              className="px-6 py-2 rounded-lg text-white font-medium hover:opacity-90 transition-opacity"
              style={{ backgroundColor: COLORS.user.primary }}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      <PurpleNavBar />
      <div className="min-h-screen bg-white">
        {/* Blue Banner Header Section */}
        <div style={{ backgroundColor: COLORS.user.primary }} className="relative overflow-hidden">
          {/* Background Pattern */}
          <div
            className="absolute right-0 top-0 bottom-0 flex items-center pointer-events-none"
            style={{ opacity: 0.15 }}
          >
            <img
              src="/new_logo_white_P.png"
              alt="Portfolio Indexing System"
              className="h-80 opacity-50"
            />
          </div>

          <div className="max-w-7xl mx-auto px-8 py-8 relative z-10">
            {/* Breadcrumbs */}
            <div className="flex items-center gap-2 text-sm text-white mb-6" style={{ opacity: 0.9 }}>
              <button onClick={() => navigate('/dashboard')} className="hover:underline">
                Dashboard
              </button>
              <span>›</span>
              <span className="font-medium">My Companies</span>
            </div>

            {/* Header Content */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white mb-1">
                  My Companies
                </h1>
                <p className="text-white text-sm" style={{ opacity: 0.9 }}>
                  {companies.length} {companies.length === 1 ? 'company' : 'companies'} • Manage your portfolio
                </p>
              </div>
              <button
                onClick={() => setShowAddCompanyModal(true)}
                className="px-6 py-2.5 rounded-lg font-medium text-white hover:bg-white hover:bg-opacity-20 transition-all"
                style={{ border: '1px solid rgba(255,255,255,0.3)' }}
              >
                + Add Company
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-6 py-6">
          {companies.length === 0 ? (
            <div className="bg-white rounded-lg border p-12 text-center" style={{ borderColor: '#E5E7EB' }}>
              <p className="text-gray-400">No companies found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Companies List */}
              <div className="lg:col-span-1">
                <div className="space-y-3">
                  {companies.map((company) => (
                    <button
                      key={company.id}
                      onClick={() => setSelectedCompany(company)}
                      className={`w-full p-4 rounded-lg border text-left transition-all ${
                        selectedCompany?.id === company.id
                          ? 'bg-blue-50'
                          : 'bg-white hover:bg-gray-50'
                      }`}
                      style={{
                        borderColor: selectedCompany?.id === company.id ? COLORS.user.primary : '#E5E7EB',
                        borderWidth: selectedCompany?.id === company.id ? '2px' : '1px'
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-12 h-12 rounded-lg flex items-center justify-center text-white text-lg font-bold flex-shrink-0"
                          style={{ backgroundColor: getInitialColor(company.name) }}
                        >
                          {getCompanyInitial(company.name)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 truncate text-sm">
                            {company.name}
                          </h3>
                          <p className="text-xs text-gray-500 truncate mt-0.5">
                            {company.industry || 'No industry'}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span 
                              className="text-xs px-2 py-0.5 rounded-full font-medium capitalize"
                              style={{ 
                                backgroundColor: company.user_role === 'owner' ? '#DBEAFE' : '#F3F4F6',
                                color: company.user_role === 'owner' ? '#1E40AF' : '#6B7280'
                              }}
                            >
                              {company.user_role}
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Right Column - Company Details */}
              <div className="lg:col-span-2">
                {selectedCompany ? (
                  <div>
                    {/* Company Header */}
                    <div className="bg-white rounded-lg border p-6 mb-6" style={{ borderColor: '#E5E7EB' }}>
                      <div className="flex items-start gap-4 mb-6">
                        <div
                          className="w-16 h-16 rounded-xl flex items-center justify-center text-white text-2xl font-bold flex-shrink-0"
                          style={{ backgroundColor: getInitialColor(selectedCompany.name) }}
                        >
                          {getCompanyInitial(selectedCompany.name)}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-start justify-between">
                            <div>
                              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                {selectedCompany.name}
                              </h2>
                              <p className="text-sm text-gray-600">
                                {selectedCompany.industry || 'Industry'} · {selectedCompany.location || 'Location'} · {selectedCompany.employees || 'N/A'} Employees
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Overview Metrics */}
                      <div className="grid grid-cols-2 gap-4">
                        {selectedCompany.location && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-1">Headquarters</p>
                            <p className="text-sm text-gray-900">{selectedCompany.location}</p>
                          </div>
                        )}
                        {selectedCompany.website && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-1">Website</p>
                            <a
                              href={formatWebsiteUrl(selectedCompany.website)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm hover:underline"
                              style={{ color: COLORS.user.primary }}
                            >
                              {selectedCompany.website}
                            </a>
                          </div>
                        )}
                        {selectedCompany.founding_year && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-1">Founded</p>
                            <p className="text-sm text-gray-900">{selectedCompany.founding_year}</p>
                          </div>
                        )}
                        {selectedCompany.employees && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-1">Employees</p>
                            <p className="text-sm text-gray-900">{selectedCompany.employees}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">Role</p>
                          <p className="text-sm text-gray-900 capitalize">{selectedCompany.user_role}</p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">Status</p>
                          <span 
                            className="text-sm font-medium"
                            style={{ color: selectedCompany.is_active ? '#10B981' : '#EF4444' }}
                          >
                            {selectedCompany.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>

                      {/* Industry Tags */}
                      {selectedCompany.industry && (
                        <div className="flex flex-wrap gap-2 mt-4">
                          {selectedCompany.industry.split(',').map((tag, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700"
                            >
                              {tag.trim()}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* About Section */}
                    <div className="bg-white rounded-lg border p-6 mb-6" style={{ borderColor: '#E5E7EB' }}>
                      <h3 className="text-lg font-bold text-gray-900 mb-4">About {selectedCompany.name}</h3>
                      {selectedCompany.description ? (
                        <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                          {selectedCompany.description}
                        </p>
                      ) : (
                        <p className="text-sm text-gray-500 italic">No description available for this company.</p>
                      )}
                    </div>

                    {/* Company Stats */}
                    <div className="bg-white rounded-lg border p-6 mb-6" style={{ borderColor: '#E5E7EB' }}>
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Company Statistics</h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">Profile Completeness</p>
                          <p className="text-2xl font-bold" style={{ color: COLORS.user.primary }}>
                            {selectedCompany.profile_completeness_score || 0}%
                          </p>
                        </div>
                        {selectedCompany.enrichment_score !== null && selectedCompany.enrichment_score !== undefined && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-1">Enrichment Score</p>
                            <p className="text-2xl font-bold" style={{ color: COLORS.user.primary }}>
                              {selectedCompany.enrichment_score}%
                            </p>
                          </div>
                        )}
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">Privacy</p>
                          <p className="text-sm font-semibold text-gray-900 mt-1">
                            {selectedCompany.is_private ? 'Private Company' : 'Public Company'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3">
                      <button
                        onClick={() => navigate(`/dashboard/companies/${selectedCompany.id}`)}
                        className="px-6 py-2.5 rounded-lg text-white font-medium text-sm transition-opacity hover:opacity-90"
                        style={{ backgroundColor: COLORS.user.primary }}
                      >
                        View Full Profile
                      </button>
                      {canManageCompany(selectedCompany) && (
                        <button
                          onClick={() => setShowMembersManager(true)}
                          className="px-6 py-2.5 rounded-lg font-medium text-sm border transition-colors hover:bg-gray-50"
                          style={{ borderColor: COLORS.user.primary, color: COLORS.user.primary }}
                        >
                          Manage Company
                        </button>
                      )}
                      {selectedCompany.website && (
                        <button
                          onClick={() => window.open(formatWebsiteUrl(selectedCompany.website!), '_blank')}
                          className="px-6 py-2.5 rounded-lg font-medium text-sm border transition-colors hover:bg-gray-50"
                          style={{ borderColor: '#E5E7EB', color: '#374151' }}
                        >
                          Visit Website
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg border p-12 text-center" style={{ borderColor: '#E5E7EB' }}>
                    <p className="text-gray-400">Select a company to view details</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Company Members Manager Drawer */}
      {showMembersManager && selectedCompany && (
        <CompanyMembersManager
          companyId={selectedCompany.id}
          currentUserRole={selectedCompany.user_role}
          onClose={() => {
            setShowMembersManager(false);
            // Reload companies to refresh user roles
            loadCompanies();
          }}
        />
      )}

      {/* Add Company Modal */}
      {showAddCompanyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">Add New Company</h3>
                <button
                  onClick={() => {
                    setShowAddCompanyModal(false);
                    setNewCompany({
                      name: '',
                      domain: '',
                      description: '',
                      website: '',
                      industry: '',
                      location: '',
                      founding_year: '',
                      employees: '',
                    });
                    setError(null);
                  }}
                  className="p-2 rounded-lg hover:bg-gray-100"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newCompany.name}
                    onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
                    placeholder="Enter company name"
                    className="w-full px-3 py-2 border rounded-lg"
                    style={{ borderColor: '#E5E7EB' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Domain <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newCompany.domain}
                    onChange={(e) => setNewCompany({ ...newCompany, domain: e.target.value })}
                    placeholder="example.com"
                    className="w-full px-3 py-2 border rounded-lg"
                    style={{ borderColor: '#E5E7EB' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={newCompany.description}
                    onChange={(e) => setNewCompany({ ...newCompany, description: e.target.value })}
                    placeholder="Company description"
                    rows={4}
                    className="w-full px-3 py-2 border rounded-lg"
                    style={{ borderColor: '#E5E7EB' }}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
                    <input
                      type="text"
                      value={newCompany.website}
                      onChange={(e) => setNewCompany({ ...newCompany, website: e.target.value })}
                      placeholder="https://example.com"
                      className="w-full px-3 py-2 border rounded-lg"
                      style={{ borderColor: '#E5E7EB' }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Industry</label>
                    <input
                      type="text"
                      value={newCompany.industry}
                      onChange={(e) => setNewCompany({ ...newCompany, industry: e.target.value })}
                      placeholder="Technology"
                      className="w-full px-3 py-2 border rounded-lg"
                      style={{ borderColor: '#E5E7EB' }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                    <input
                      type="text"
                      value={newCompany.location}
                      onChange={(e) => setNewCompany({ ...newCompany, location: e.target.value })}
                      placeholder="City, Country"
                      className="w-full px-3 py-2 border rounded-lg"
                      style={{ borderColor: '#E5E7EB' }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Founding Year</label>
                    <input
                      type="number"
                      value={newCompany.founding_year}
                      onChange={(e) => setNewCompany({ ...newCompany, founding_year: e.target.value })}
                      placeholder="2020"
                      className="w-full px-3 py-2 border rounded-lg"
                      style={{ borderColor: '#E5E7EB' }}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Employees</label>
                  <input
                    type="text"
                    value={newCompany.employees}
                    onChange={(e) => setNewCompany({ ...newCompany, employees: e.target.value })}
                    placeholder="1-10, 11-50, 51-100, etc."
                    className="w-full px-3 py-2 border rounded-lg"
                    style={{ borderColor: '#E5E7EB' }}
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleAddCompany}
                    disabled={isCreatingCompany}
                    className="flex-1 px-4 py-2 rounded-lg text-white font-medium disabled:opacity-50"
                    style={{ backgroundColor: COLORS.user.primary }}
                  >
                    {isCreatingCompany ? 'Creating...' : 'Create Company'}
                  </button>
                  <button
                    onClick={() => {
                      setShowAddCompanyModal(false);
                      setNewCompany({
                        name: '',
                        domain: '',
                        description: '',
                        website: '',
                        industry: '',
                        location: '',
                        founding_year: '',
                        employees: '',
                      });
                      setError(null);
                    }}
                    className="flex-1 px-4 py-2 rounded-lg border font-medium"
                    style={{ borderColor: '#E5E7EB' }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
